import React, { useState, useEffect } from 'react'
import { fetchAccommodations, fetchStaysForPitch, fetchGuestInventories } from '../Inventory/Field/fieldApi'
import { findAccommodation } from '../Inventory/Field/fieldModel'
import { formatShortDate, formatRange, nowSeconds } from '../Inventory/Field/fieldDates'
import { entriesAt, stateOfEntries, zoneLabel, zoneOf } from '../Inventory/Field/fieldTours'
import { shortName } from '../Inventory/Field/fieldLabels'
import { canRead, canWrite } from '../UI/dashboardAccess'
import { familyLabel, isCommonZone, isOpenIntervention, assigneeName, STATUS_LABELS, guestInventorySummary, currentAndNextStay, startedStays, groupByStay, seasonsOf, inSeason } from './locationsData'

// Fiche d'un emplacement, copie conforme du panneau de la maquette D5 (canvas « Plan des hébergements ») :
// en-tête, bloc « Dans ce tour », séjours, puis deux onglets Inventaires et Interventions, lignes
// regroupées par séjour, filtre de saison à partir de deux saisons. Le plan reste visible à gauche.
// Réel : séjours, interventions, inventaires des vacanciers. Tour : simulé dans ce navigateur.

const COLORS = { text: '#0F172A', muted: '#475569', body: '#334155', border: '#E2E8F0', primary: '#000095', soft: '#E8EBF5' }
const MONO = "ui-monospace, 'SF Mono', Menlo, Consolas, monospace"
const CHIP = { alignSelf: 'flex-start', padding: '2px 10px', borderRadius: 999, fontSize: 13, fontWeight: 700 }
const ROW = { display: 'flex', gap: 14, padding: '14px 10px', margin: '0 -10px', borderRadius: 12, borderBottom: '1px solid #F1F5F9' }
const TOUR_BOX = {
	intervention: { background: '#FFF4DB', border: '2px solid #FFB82E' },
	done: { background: '#ECFDF5', border: '2px solid #34D399' },
	todo: { background: '#F1F5F9', border: '2px solid #94A3B8' }
}
const LINK = { backgroundImage: 'none', backgroundColor: 'transparent', alignSelf: 'flex-start', minHeight: 32, border: 0, padding: 0, fontSize: 14, fontWeight: 700, color: COLORS.primary, textDecoration: 'underline', textUnderlineOffset: 3, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }

// Largeur du tiroir ; la page lui laisse cette place à droite
export const PANEL_WIDTH = 480

// Modèles d'hébergement gardés le temps de la session
let accommodationsCache = null

function statusChip(intervention)
{
	const urgent = intervention.urgency === 'CRITICAL' && isOpenIntervention(intervention)
	const label = (STATUS_LABELS[intervention.status] || intervention.status) + (urgent ? ', urgente' : '')
	let look = { background: '#FEF3C7', color: '#78350F' }
	if (urgent || intervention.status === 'BLOCKED')
		look = { background: '#FFE4E6', color: '#881337' }
	else if (intervention.status === 'IN_PROGRESS')
		look = { background: '#DBEAFE', color: '#1E3A8A' }
	else if (intervention.status === 'DONE')
		look = { background: '#F1F5F9', color: '#334155' }
	return { label, look }
}

function SeasonSelect({ id, seasons, value, onChange })
{
	if (seasons.length < 2)
		return null
	return (
		<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
			<label htmlFor={id} style={{ fontSize: 14, color: COLORS.muted, fontWeight: 600 }}>Saison</label>
			<select id={id} className="browser-default" value={value} onChange={(e) => onChange(e.target.value)} style={{ minHeight: 40, padding: '0 12px', borderRadius: 10, border: '1px solid #CBD5E1', background: '#FFFFFF', fontSize: 15, color: COLORS.text, fontFamily: 'inherit' }}>
				<option value="all">Toutes les saisons</option>
				{seasons.map(year => <option key={year} value={String(year)}>{'Saison ' + year}</option>)}
			</select>
		</div>
	)
}

function Group({ title, children })
{
	return (
		<div style={{ display: 'flex', flexDirection: 'column' }}>
			<h3 style={{ fontSize: 14, fontWeight: 700, color: COLORS.body, padding: '8px 0', borderBottom: '1px solid ' + COLORS.border, margin: 0 }}>{title}</h3>
			<ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>{children}</ul>
		</div>
	)
}

const RowIcon = ({ label, children }) => (
	<span role="img" aria-label={label} style={{ width: 40, height: 40, flex: 'none', borderRadius: 999, background: COLORS.soft, color: COLORS.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{children}</span>
)

const ClipboardIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="5" y="4" width="14" height="17" rx="2" /><path d="M9 4h6v3H9z" /><path d="M9 12h6M9 16h4" /></svg>
const WrenchIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14.5 6.5a4 4 0 0 0 5 5L12 19a2.1 2.1 0 0 1-3-3z" /></svg>

// « n° 3488, du 15 au 22 août (départ aujourd'hui) »
function stayLine(stay, now, empty)
{
	if (!stay)
		return empty
	const today = new Date(now * 1000).toDateString()
	let tag = ''
	if (new Date(stay.departure_time * 1000).toDateString() === today)
		tag = ' (départ aujourd\'hui)'
	else if (new Date(stay.arrival_time * 1000).toDateString() === today)
		tag = ' (arrivée aujourd\'hui)'
	return 'n° ' + stay.stay_number + ', du ' + formatRange(stay.arrival_time, stay.departure_time, now) + tag
}

// interventions : celles de l'emplacement ; tour : tour en cours (simulé dans ce navigateur) ou null ;
// history : ouvrir un ticket (/interventions/<id>) ou en créer un sur ce lieu (?lieu=<id>)
export default function LocationPanel({ pitch, interventions, tour, user, history, onClose })
{
	const [now] = useState(nowSeconds())
	const [model, setModel] = useState(undefined)
	const [stays, setStays] = useState(null)
	const [inventories, setInventories] = useState(null)
	const [error, setError] = useState(false)
	const [tab, setTab] = useState(null)
	const [seasonInv, setSeasonInv] = useState('all')
	const [seasonItv, setSeasonItv] = useState('all')

	useEffect(() => {
		let cancelled = false
		const accommodations = accommodationsCache ? Promise.resolve(accommodationsCache) : fetchAccommodations()
		accommodations
			.then(list => {
				accommodationsCache = list
				if (!cancelled)
					setModel(findAccommodation(list, pitch.location_category || ''))
			})
			.catch(() => {
				if (!cancelled)
					setModel(null)
			})
		fetchStaysForPitch(pitch.location)
			.then(list => {
				if (cancelled)
					return null
				setStays(list)
				return Promise.all(startedStays(list, now).map(stay => fetchGuestInventories(stay.stay_number).then(found => found.map(inventory => ({ ...inventory, stay })))))
					.then(lists => {
						if (!cancelled)
							setInventories([].concat(...lists))
					})
			})
			.catch(() => {
				if (!cancelled)
					setError(true)
			})
		return () => {
			cancelled = true
		}
	}, [pitch, now])

	useEffect(() => {
		const onKey = (e) => {
			if (e.key === 'Escape')
				onClose()
		}
		window.addEventListener('keydown', onKey)
		return () => window.removeEventListener('keydown', onKey)
	}, [onClose])

	const permissions = user ? user.permissions : null
	const canOpenTickets = !!history && canRead(permissions, 'interventions')
	const canCreate = !!history && canWrite(permissions, 'interventions') && !!pitch._id
	const range = (arrival, departure) => 'du ' + formatRange(arrival, departure, now)
	const { current, next } = currentAndNextStay(stays, now)
	const zone = zoneOf(pitch)

	// Lignes des deux onglets
	const itvItems = (interventions || []).map(item => ({ item, time: item.created_time || 0, stayNumber: item.stay_number, open: isOpenIntervention(item) }))
	const invItems = (inventories || []).map(inventory => ({ inventory, summary: guestInventorySummary(inventory), time: inventory.create_time || 0, stayNumber: inventory.stay ? inventory.stay.stay_number : null, open: false }))
	const openTotal = itvItems.filter(row => row.open).length
	const noModel = model === null && invItems.length === 0
	const activeTab = tab || (openTotal > 0 || noModel ? 'itv' : 'inv')
	const lastInventory = invItems.slice().sort((a, b) => b.time - a.time)[0] || null

	// Bloc « Dans ce tour »
	const tourEntries = tour ? entriesAt(tour, pitch.location) : []
	const tourState = stateOfEntries(tourEntries)
	const latest = tourEntries.slice().sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))[0] || null
	const tourInventory = tourEntries.filter(entry => entry.kind === 'inventory' || !entry.kind).pop() || null
	const tourInterventions = tourEntries.filter(entry => entry.kind === 'intervention').length
	let tourInv = ''
	if (tourInventory)
		tourInv = 'Inventaire du tour : ' + (tourInventory.gaps ? tourInventory.gaps + (tourInventory.gaps > 1 ? ' écarts' : ' écart') : 'aucun écart')
	else if (latest)
		tourInv = model ? 'Pas d\'inventaire pendant ce passage' : 'Pas d\'inventaire : pas de modèle pour cette catégorie'

	const TAB = (on) => ({ backgroundImage: 'none', backgroundColor: 'transparent', minHeight: 44, padding: '0 14px', fontSize: 15, fontWeight: 700, marginBottom: -1, border: 0, borderBottom: '3px solid ' + (on ? COLORS.text : 'transparent'), color: on ? COLORS.text : COLORS.muted, cursor: 'pointer', fontFamily: 'inherit' })

	return (
		// Tiroir collé au bord droit de l'écran, du haut en bas (retour d'Alexandra : il flottait dans la page)
		<aside aria-label={'Fiche de l\'emplacement ' + pitch.location} style={{ position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 40, width: PANEL_WIDTH, maxWidth: '100vw', overflowY: 'auto', overscrollBehavior: 'contain', boxSizing: 'border-box', background: '#FFFFFF', borderLeft: '1px solid ' + COLORS.border, boxShadow: '-12px 0 32px rgba(15, 23, 42, 0.08)', padding: '28px 28px 40px', display: 'flex', flexDirection: 'column', gap: 20, fontSize: 15, color: COLORS.text }}>
			<header style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
				<div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
					<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
						<h2 style={{ fontFamily: MONO, fontSize: 32, fontWeight: 700, lineHeight: 1.1, margin: 0 }}>{pitch.location}</h2>
						<div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
							<span style={{ display: 'inline-flex', alignItems: 'center', minHeight: 28, padding: '0 10px', borderRadius: 999, background: COLORS.soft, color: COLORS.primary, fontSize: 14, fontWeight: 700 }}>{familyLabel(pitch)}</span>
							{zone && !isCommonZone(pitch) && <span style={{ fontSize: 15, fontWeight: 600, color: COLORS.body }}>{zoneLabel(zone)}</span>}
						</div>
					</div>
					<button type="button" aria-label="Fermer la fiche" onClick={onClose} style={{ backgroundImage: 'none', backgroundColor: '#F1F5F9', width: 44, height: 44, borderRadius: 12, border: 0, color: COLORS.text, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none', cursor: 'pointer' }}>
						<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
					</button>
				</div>
				<dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'minmax(0, 150px) minmax(0, 1fr)', gap: '8px 16px' }}>
					<dt style={{ color: COLORS.muted }}>Catégorie</dt><dd style={{ margin: 0, fontWeight: 600 }}>{(pitch.location_category || '').trim() || 'Non renseignée'}</dd>
					<dt style={{ color: COLORS.muted }}>Capacité</dt><dd style={{ margin: 0, fontWeight: 600 }}>{pitch.capacity ? pitch.capacity + (pitch.capacity > 1 ? ' personnes' : ' personne') : 'Non renseignée'}</dd>
					<dt style={{ color: COLORS.muted }}>Modèle d'inventaire</dt><dd style={{ margin: 0, fontWeight: 600 }}>{model === undefined ? '…' : (model ? model.name : 'Aucun')}</dd>
				</dl>
			</header>

			{tour && (
				<section aria-label="Dans ce tour" style={{ borderRadius: 14, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 6, ...TOUR_BOX[tourState] }}>
					<h3 style={{ fontSize: 15, fontWeight: 800, margin: 0 }}>Dans ce tour</h3>
					{latest && <p style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{'Fait le ' + formatShortDate(latest.timestamp, now) + ', par ' + (shortName(latest.user_name) || 'un collègue')}</p>}
					{latest && tourInv && <p style={{ margin: 0, fontSize: 15, color: COLORS.body }}>{tourInv}</p>}
					{latest && tourInterventions > 0 && <button type="button" onClick={() => setTab('itv')} style={LINK}>{tourInterventions + (tourInterventions > 1 ? ' interventions créées depuis le tour' : ' intervention créée depuis le tour')}</button>}
					{latest && tourInterventions === 0 && <p style={{ margin: 0, fontSize: 15, color: COLORS.body }}>Aucune intervention créée depuis le tour</p>}
					{!latest && <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: COLORS.body }}>Pas encore fait dans ce tour</p>}
				</section>
			)}

			<section aria-label="Séjours" style={{ background: '#F8FAFC', border: '1px solid ' + COLORS.border, borderRadius: 14, padding: '14px 16px', display: 'grid', gridTemplateColumns: 'minmax(0, 88px) minmax(0, 1fr)', gap: '8px 12px' }}>
				<span style={{ color: COLORS.muted, fontWeight: 600 }}>En cours</span><span style={{ fontWeight: 600 }}>{stays ? stayLine(current, now, 'Aucun séjour en cours') : (error ? 'Séjours indisponibles' : '…')}</span>
				<span style={{ color: COLORS.muted, fontWeight: 600 }}>À venir</span><span style={{ fontWeight: 600 }}>{stays ? stayLine(next, now, 'Aucun séjour à venir') : (error ? 'Séjours indisponibles' : '…')}</span>
			</section>

			<div role="tablist" aria-label="Historique de l'emplacement" style={{ display: 'flex', gap: 4, borderBottom: '1px solid ' + COLORS.border }}>
				<button type="button" role="tab" aria-selected={activeTab === 'inv'} onClick={() => setTab('inv')} style={TAB(activeTab === 'inv')}>{'Inventaires (' + invItems.length + ')'}</button>
				<button type="button" role="tab" aria-selected={activeTab === 'itv'} onClick={() => setTab('itv')} style={TAB(activeTab === 'itv')}>{'Interventions (' + itvItems.length + ')'}</button>
			</div>

			{activeTab === 'inv' && (
				<div role="tabpanel" aria-label="Inventaires" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
					{noModel && <p style={{ margin: 0, color: COLORS.muted, padding: 16, borderRadius: 14, background: '#F8FAFC', border: '1px dashed #CBD5E1' }}>Pas de modèle d'inventaire pour cette catégorie.</p>}
					{error && <p style={{ margin: 0, color: COLORS.muted, padding: '8px 0' }}>Impossible de charger les inventaires. Vérifiez la connexion.</p>}
					{!error && inventories === null && <p style={{ margin: 0, color: COLORS.muted, padding: '8px 0' }}>Chargement des inventaires…</p>}
					{!error && inventories !== null && !noModel && invItems.length === 0 && <p style={{ margin: 0, color: COLORS.muted, padding: '8px 0' }}>Aucun inventaire déposé.</p>}
					{lastInventory && lastInventory.summary.missing.length > 0 && (
						<div style={{ borderRadius: 14, background: '#FFFBEB', border: '1px solid #FCD34D', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
							<h3 style={{ fontSize: 15, fontWeight: 700, color: '#78350F', margin: 0 }}>{'Objets manquants au dernier inventaire, ' + formatShortDate(lastInventory.time, now)}</h3>
							<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 600, color: '#78350F' }}><span>Objet</span><span>Compté / attendu</span></div>
							<ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
								{lastInventory.summary.missing.map((object, index) => (
									<li key={index} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 15, color: '#422006' }}>
										<span>{object.name}</span>
										<span style={{ fontFamily: MONO, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{object.count + ' / ' + object.quantity}</span>
									</li>
								))}
							</ul>
						</div>
					)}
					<SeasonSelect id="location-season-inv" seasons={seasonsOf(invItems)} value={seasonInv} onChange={setSeasonInv} />
					{groupByStay(invItems.filter(row => inSeason(row, seasonInv)), stays, range).map(group => (
						<Group key={group.key} title={group.title}>
							{group.items.map((row, index) => {
								const missing = row.summary.missing.length
								return (
									<li key={index} style={ROW}>
										<RowIcon label="Inventaire"><ClipboardIcon /></RowIcon>
										<div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
											<span style={{ fontSize: 15, fontWeight: 700 }}>{row.inventory.type === 'departure' ? 'Inventaire de sortie' : 'Inventaire d\'arrivée'}</span>
											<span style={{ fontSize: 15, color: COLORS.body }}>{formatShortDate(row.time, now) + ', Vacancier'}</span>
											<span style={{ ...CHIP, ...(missing === 0 ? { background: '#D1FAE5', color: '#064E3B' } : { background: '#FEF3C7', color: '#78350F' }) }}>{missing === 0 ? 'Aucun écart' : missing + (missing > 1 ? ' écarts' : ' écart')}</span>
										</div>
									</li>
								)
							})}
						</Group>
					))}
				</div>
			)}

			{activeTab === 'itv' && (
				<div role="tabpanel" aria-label="Interventions" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
					{canCreate && (
						<button type="button" onClick={() => history.push('/interventions?lieu=' + encodeURIComponent(pitch._id))} style={{ backgroundImage: 'none', backgroundColor: COLORS.primary, alignSelf: 'flex-start', minHeight: 44, padding: '0 16px', borderRadius: 12, border: 0, color: '#FFFFFF', fontSize: 15, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontFamily: 'inherit' }}>
							<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
							Créer une intervention
						</button>
					)}
					{itvItems.length === 0 && <p style={{ margin: 0, color: COLORS.muted, padding: '8px 0' }}>Aucune intervention.</p>}
					<SeasonSelect id="location-season-itv" seasons={seasonsOf(itvItems)} value={seasonItv} onChange={setSeasonItv} />
					{groupByStay(itvItems.filter(row => inSeason(row, seasonItv)), stays, range).map(group => (
						<Group key={group.key} title={group.title}>
							{group.items.map(row => {
								const item = row.item
								const chip = statusChip(item)
								const team = assigneeName(item)
								const fromTour = !!tour && !!item.tour_id && item.tour_id === tour._id
								const title = item.label || item.description || ''
								return (
									<li key={item._id || row.time} style={ROW}>
										<RowIcon label="Intervention"><WrenchIcon /></RowIcon>
										<div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
											{item.type && <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.muted }}>{item.type}</span>}
											<span style={{ fontSize: 15, fontWeight: 700, overflowWrap: 'anywhere' }}>{title.charAt(0).toUpperCase() + title.slice(1)}</span>
											<span style={{ fontSize: 15, color: COLORS.body }}>{['Créée le ' + formatShortDate(row.time, now), team].filter(Boolean).join(', ')}</span>
											<span style={{ ...CHIP, ...chip.look }}>{chip.label}</span>
											{fromTour && <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.primary }}>{'Depuis le ' + tour.name.charAt(0).toLowerCase() + tour.name.slice(1)}</span>}
											{canOpenTickets && item._id && <button type="button" onClick={() => history.push('/interventions/' + encodeURIComponent(item._id))} style={LINK}>Ouvrir le ticket</button>}
										</div>
									</li>
								)
							})}
						</Group>
					))}
				</div>
			)}
		</aside>
	)
}
